import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronRight, Star, Sparkles } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { toast } from "sonner";
import { PHONE_DISPLAY, telLink, whatsappLink, buildBookingMessage } from "@/lib/whatsapp";
import { createBooking } from "@/lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1713682995521-22ec819b50ac?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1800";

const HEADLINE_LINES = ["Your Journey,", "Our Priority."];

const VEHICLES = [
  "Swift Dzire",
  "Toyota Etios",
  "Maruti Ertiga",
  "Toyota Innova",
  "Innova Crysta",
  "Tempo Traveller",
  "Luxury Car",
];

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 140]);
  const scaleImg = useTransform(scrollY, [0, 800], [1, 1.08]);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    pickup_location: "",
    drop_location: "",
    pickup_date: "",
    pickup_time: "",
    vehicle_type: "Swift Dzire",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.pickup_location) {
      toast.error("Please fill name, mobile & pickup location.");
      return;
    }
    setLoading(true);
    try {
      await createBooking(form);
    } catch (err) {
      // continue even if backend save fails
      console.error(err);
    } finally {
      setLoading(false);
    }
    const msg = buildBookingMessage(form);
    toast.success("Opening WhatsApp with your booking details...");
    window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <section id="home" className="relative min-h-[100svh] w-full overflow-hidden bg-brand-blue text-white" data-testid="hero-section">
      {/* Parallax background image */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 -z-0">
        <img src={HERO_IMAGE} alt="Rajasthan Palace at Sunset" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-brand-blue/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left_top,rgba(11,77,184,0.4),transparent_60%)]" />
      </motion.div>

      <div className="grain absolute inset-0 pointer-events-none z-[1]" aria-hidden />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 pt-32 md:pt-36 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* LEFT: Hero copy */}
          <div className="lg:col-span-7 pt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 text-xs uppercase tracking-[0.24em] font-semibold"
              data-testid="hero-eyebrow"
            >
              <Sparkles className="w-3.5 h-3.5 text-brand-orange" />
              Jodhpur · Rajasthan · Since 2012
            </motion.div>

            <h1 className="mt-6 font-display font-black leading-[0.95] tracking-tight text-white text-[52px] sm:text-[72px] md:text-[96px] lg:text-[112px]" data-testid="hero-title">
              {HEADLINE_LINES.map((line, i) => (
                <div key={i} className="mask-reveal block">
                  <span style={{ animationDelay: `${0.35 + i * 0.18}s` }} className="block">
                    {i === 1 ? (
                      <>
                        <span className="italic font-medium text-brand-orange">Our</span>{" "}
                        <span>Priority.</span>
                      </>
                    ) : (
                      line
                    )}
                  </span>
                </div>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-6 max-w-xl text-white/85 text-base md:text-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              Safe, comfortable & affordable taxi services in Jodhpur. Book local cabs, airport transfers, outstation rides and hand-crafted Rajasthan tour packages — with professional drivers who know every road home.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.55 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a
                href={telLink()}
                data-testid="hero-call-button"
                className="btn-shine inline-flex items-center gap-2 rounded-full bg-white text-brand-blue px-6 py-3.5 text-sm font-semibold hover:bg-brand-orange hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
              </a>
              <a
                href={whatsappLink("Hi Mamta Tours, I'd like to book a cab.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="btn-shine inline-flex items-center gap-2 rounded-full bg-brand-orange text-white px-6 py-3.5 text-sm font-semibold hover:bg-white hover:text-brand-blue transition-colors"
              >
                <WhatsAppIcon size={16} /> WhatsApp Now
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.25, duration: 0.5 }}
              className="mt-10 flex items-center gap-4"
            >
              <div className="flex -space-x-2">
                {["1544005313-94ddf0286df2", "1502685104226-ee32379fefbe", "1508214751196-bcfd4ca60f91", "1531123897727-8f129e1688ce"].map((id, i) => (
                  <img
                    key={i}
                    src={`https://images.unsplash.com/photo-${id}?w=80&h=80&fit=crop&crop=faces&q=80`}
                    alt="Happy customer"
                    className="w-9 h-9 rounded-full ring-2 ring-white/90 object-cover"
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-brand-orange">
                  {[0,1,2,3,4].map((s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                </div>
                <div className="text-white/85 text-xs mt-1" data-testid="hero-social-proof">
                  Trusted by <span className="font-semibold text-white">1000+</span> happy customers · 4.9/5 rated
                </div>
              </div>
            </motion.div>
          </div>

          {/* RIGHT: Booking form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="lg:col-span-5 lg:mt-2"
          >
            <div className="relative rounded-[28px] bg-white/95 backdrop-blur-xl text-brand-ink p-5 md:p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] border border-white/60" data-testid="booking-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-brand-blue">Instant Booking</div>
                  <div className="font-display text-2xl md:text-[26px] font-bold mt-1">Reserve your ride</div>
                </div>
                <div className="hidden md:flex items-center gap-2 text-xs bg-brand-blue/10 text-brand-blue rounded-full px-3 py-1.5 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live 24/7
                </div>
              </div>

              <form onSubmit={submit} className="mt-5 grid grid-cols-2 gap-3" data-testid="booking-form">
                <Field label="Full Name" name="name" value={form.name} onChange={onChange} placeholder="Your name" span={2} required />
                <Field label="Mobile Number" name="mobile" value={form.mobile} onChange={onChange} placeholder="+91 ..." type="tel" required />
                <div className="col-span-1">
                  <Label>Vehicle</Label>
                  <select
                    name="vehicle_type"
                    value={form.vehicle_type}
                    onChange={onChange}
                    data-testid="booking-vehicle"
                    className="mt-1 w-full h-11 rounded-xl border border-brand-ink/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  >
                    {VEHICLES.map((v) => <option key={v}>{v}</option>)}
                  </select>
                </div>
                <Field label="Pickup Location" name="pickup_location" value={form.pickup_location} onChange={onChange} placeholder="e.g. Jodhpur Airport" span={2} required />
                <Field label="Drop Location" name="drop_location" value={form.drop_location} onChange={onChange} placeholder="e.g. Jaisalmer" span={2} />
                <Field label="Pickup Date" name="pickup_date" value={form.pickup_date} onChange={onChange} type="date" />
                <Field label="Pickup Time" name="pickup_time" value={form.pickup_time} onChange={onChange} type="time" />
                <div className="col-span-2">
                  <Label>Message</Label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={2}
                    data-testid="booking-message"
                    placeholder="Any special requests…"
                    className="mt-1 w-full rounded-xl border border-brand-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="btn-shine col-span-2 mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white h-12 font-semibold hover:bg-brand-orange transition-colors disabled:opacity-70"
                >
                  {loading ? "Sending..." : (<>Book Now <ChevronRight className="w-4 h-4" /></>)}
                </button>
                <p className="col-span-2 text-[11px] text-brand-ink/60 text-center">By booking you agree to receive a WhatsApp confirmation from us.</p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-1 text-white/70 text-[10px] tracking-[0.32em] uppercase">
        <span>Scroll</span>
        <span className="block w-px h-8 bg-white/40 overflow-hidden">
          <span className="block w-px h-8 bg-white animate-[rise_1.6s_linear_infinite]" />
        </span>
      </div>
    </section>
  );
}

function Label({ children }) {
  return <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-ink/60">{children}</label>;
}

function Field({ label, name, value, onChange, placeholder, type = "text", required, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : "col-span-1"}>
      <Label>{label}{required && <span className="text-brand-orange"> *</span>}</Label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={`booking-${name}`}
        className="mt-1 w-full h-11 rounded-xl border border-brand-ink/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 placeholder:text-brand-ink/40"
      />
    </div>
  );
}
